/**
 * @file Hook wrapping the `getProductAndPriceAndAvailability` callable. Consumers:
 * `useSignifyProductModal` and `useSignifyConfigurationSidebar`, which need product, price and
 * availability together so the BOM line-item pipeline can fill `leadTimes` from
 * `defaultPlannedDeliveryTime` alongside the product- and price-derived fields.
 * `execute` rethrows the callable's own error, so this hook adds no message of its own and the
 * caller sees the `HttpsError` intact; `error` stays populated for the caller's banner.
 */

import { useCallback } from 'react';
import { ProductAndPriceAndAvailabilityResponse, SIGNIFY_CONFIG } from '#types/SignifyApi.types';
import { getProductAndPriceAndAvailability } from '../services/signifyApiService';
import { useSignifyApi } from './useSignifyApi';

export const useSignifyProductPriceAndAvailability = () => {
  const api = useSignifyApi<ProductAndPriceAndAvailabilityResponse>();

  const fetchProductPriceAndAvailability = useCallback(
    async (
      productCode: string,
      fields: 'BASIC' | 'DEFAULT' | 'FULL' = SIGNIFY_CONFIG.defaultFields,
      customerId?: string,
      distributorId?: string,
      configurationId?: string,
    ): Promise<ProductAndPriceAndAvailabilityResponse> =>
      api.execute(() =>
        getProductAndPriceAndAvailability(
          productCode,
          fields,
          customerId,
          distributorId,
          configurationId,
        ),
      ),
    [api],
  );

  return {
    loading: api.loading,
    error: api.error,
    productPriceAndAvailabilityData: api.data,
    fetchProductPriceAndAvailability,
    clearProductPriceAndAvailability: api.clear,
  };
};
